import { createRouter, createWebHistory } from 'vue-router'
import { useTitle } from '@vueuse/core'
import HomeView from '../views/home/HomeView.vue'
import { verifyPhoneBind, verifyUserBeforeTrade, verifyPaymentPassword } from '@/utils'
import { csManager } from '@/utils/customerService'
import { useSessionStore } from '@/stores/session'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    // 否则滚动到顶部
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { title: '首页' },
    },
    {
      path: '/quick-buy-coin',
      name: 'quick-buy-coin',
      component: () => import('../views/quick-buy-coin/QuickBuyCoin.vue'),
      meta: { title: '快速买币', loginRequired: true },
    },
    {
      path: '/quick-buy-coin/failure',
      name: 'quick-buy-coin-failure',
      component: () => import('../views/quick-buy-coin/QuickBuyCoinFailure.vue'),
      meta: { title: '快速买币失败', loginRequired: true },
    },
    {
      path: '/quick-sell-coin',
      name: 'quick-sell-coin',
      component: () => import('../views/quick-sell-coin/QuickSellCoin.vue'),
      meta: { title: '快速卖币', loginRequired: true },
    },
    {
      path: '/quick-sell-coin/order-list',
      name: 'quick-sell-coin-order-list',
      component: () => import('../views/quick-sell-coin/QuickSellCoinOrderList.vue'),
      meta: { title: '卖币订单', loginRequired: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/login/LoginView.vue'),
      meta: { title: '登录' },
    },
    {
      path: '/login_v2',
      name: 'login_v2',
      component: () => import('../views/login/LoginV2View.vue'),
      meta: { title: '登录' },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/register/RegisterView.vue'),
      meta: { title: '注册' },
    },
    {
      path: '/qr-scanner',
      name: 'QRScanner',
      component: () => import('../views/qr-scanner/QRScanner.vue'),
      meta: { title: '扫一扫' },
    },
    {
      path: '/reset-pwd/step1',
      name: 'ResetPasswordStep1',
      component: () => import('@/views/reset-pwd/ResetPasswordStep1.vue'),
      meta: { title: '重置密码' },
    },
    {
      path: '/reset-pwd/step2',
      name: 'ResetPasswordStep2',
      component: () => import('@/views/reset-pwd/ResetPasswordStep2.vue'),
      meta: { title: '安全验证', requiresResetPhone: true },
    },
    {
      path: '/reset-pwd/step3',
      name: 'ResetPasswordStep3',
      component: () => import('@/views/reset-pwd/ResetPasswordStep3.vue'),
      meta: { title: '设置新密码', requiresResetToken: true },
    },
    {
      path: '/trade',
      name: 'trade',
      component: () => import('../views/c2c/TradeView.vue'),
      meta: { title: '买卖币', loginRequired: true },
    },
    {
      path: '/usdt-buy-coin',
      name: 'usdt-buy-coin',
      component: () => import('../views/c2c/UsdtBuyCoinView.vue'),
      meta: { title: 'USDT买LB', loginRequired: true },
    },
    {
      path: '/usdt-buy-coin/success',
      name: 'usdt-buy-coin-success',
      component: () => import('../views/c2c/USDTBuyCoinViewSuc.vue'),
      meta: { title: '购买成功', loginRequired: true },
    },
    {
      path: '/usdt-buy-coin/records',
      name: 'usdt-buy-coin-records',
      component: () => import('../views/c2c/USDTBuyCoinRecordsView.vue'),
      meta: { title: '兑换记录', loginRequired: true },
    },
    {
      path: '/buy-coin/:id',
      name: 'buy-coin-detail',
      component: () => import('../views/c2c/BuyOrderDetailView.vue'),
      meta: { title: '购买详情', loginRequired: true },
      async beforeEnter(_to, _from, next) {
        try {
          await verifyUserBeforeTrade()
          next()
        } catch {
          next(false)
        }
      },
    },
    {
      path: '/sell-coin',
      name: 'sell-coin',
      component: () => import('../views/c2c/SellCoinView.vue'),
      meta: { title: '卖币', loginRequired: true },
    },
    {
      path: '/sell-coin/:id',
      name: 'sell-coin-detail',
      component: () => import('../views/c2c/SellCoinDetailView.vue'),
      meta: { title: '出售详情', loginRequired: true },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('../views/orders/OrdersView.vue'),
      meta: { title: '订单', loginRequired: true },
    },
    {
      path: '/order/:order_id',
      name: 'order-detail',
      component: () => import('../views/orders/OrderDetailView.vue'),
      meta: { title: '订单详情', loginRequired: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/profile/ProfileView.vue'),
      meta: { title: '个人中心', loginRequired: true },
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/chat/ChatView.vue'),
      meta: { title: '聊天', loginRequired: true },
    },
    {
      path: '/transfer',
      name: 'transfer',
      component: () => import('../views/transfer/TransferView.vue'),
      meta: { title: '转账', loginRequired: true },
    },
    {
      path: '/user',
      name: 'user',
      meta: { loginRequired: true },
      children: [
        {
          path: 'verify-real',
          name: 'VerifyReal',
          component: () => import('../views/user/verify-real/VerifyReal.vue'),
          meta: { title: '实名认证' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'phone-bind',
          name: 'BindPhoneStep1',
          component: () => import('../views/user/bind-phone/BindPhoneStep1.vue'),
          meta: { title: '绑定手机/邮箱' },
        },
        {
          path: 'phone-bind/step2',
          name: 'BindPhoneStep2',
          component: () => import('../views/user/bind-phone/BindPhoneStep2.vue'),
          meta: { title: '安全验证' },
        },
        {
          path: 'set-pin',
          name: 'SetPin',
          component: () => import('../views/user/set-pin/SetPin.vue'),
          meta: { title: '设置支付密码' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              // await verifyKYC()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'reset-pin/step1',
          redirect: { name: 'ResetPinStep2' },
        },
        {
          path: 'reset-pin/step2',
          name: 'ResetPinStep2',
          component: () => import('../views/user/reset-pin/ResetPinStep2.vue'),
          meta: { title: '安全验证' },
        },
        {
          path: 'reset-pin/step3',
          name: 'ResetPinStep3',
          component: () => import('../views/user/reset-pin/ResetPinStep3.vue'),
          meta: { title: '设置新支付密码', requiresResetToken: true },
        },
        {
          path: 'pay-methods',
          name: 'PayMethods',
          component: () => import('../views/user/pay-methods/PayMethods.vue'),
          meta: { title: '支付方式' },
        },
        {
          path: 'pay-methods/add',
          name: 'AddPayMethod',
          component: () => import('../views/user/pay-methods/AddPayMethod.vue'),
          meta: { title: '添加支付方式' },
          async beforeEnter(_to, _from, next) {
            try {
              await verifyPhoneBind()
              await verifyPaymentPassword()
              next()
            } catch {
              next(false)
            }
          },
        },
        {
          path: 'edit-nickname',
          name: 'EditNickname',
          component: () => import('../views/user/edit-nickname/EditNickname.vue'),
          meta: { title: '修改昵称' },
        },
        {
          path: 'profile-info',
          name: 'UserProfile',
          component: () => import('../views/user/profile/UserProfileView.vue'),
          meta: { title: '个人信息' },
        },
        {
          path: 'payment-qrcode',
          name: 'PaymentQRCode',
          component: () => import('../views/user/payment-qrcode/PaymentQRCode.vue'),
          meta: { title: '收款码' },
        },
        {
          path: 'security',
          name: 'SecurityCenter',
          component: () => import('../views/user/security/SecurityCenterView.vue'),
          meta: { title: '安全中心' },
        },
        {
          path: 'devices',
          name: 'Devices',
          component: () => import('../views/user/security/DevicesView.vue'),
          meta: { title: '设备' },
        },
        {
          path: 'bills',
          name: 'Bills',
          component: () => import('../views/user/bills/BillsView.vue'),
          meta: { title: '我的账单' },
        },
        {
          path: 'reports',
          name: 'Reports',
          component: () => import('../views/user/reports/ReportsView.vue'),
          meta: { title: '举报列表' },
        },
        {
          path: 'appeals',
          name: 'Appeals',
          component: () => import('../views/profile/AppealListView.vue'),
          meta: { title: '申诉列表' },
        },
        {
          path: 'business',
          name: 'Business',
          component: () => import('../views/user/business/Business.vue'),
          meta: { title: '商家认证' },
        },
        {
          path: 'notify',
          name: 'NotifyCenter',
          component: () => import('../views/user/notify/NotifyCenterView.vue'),
          meta: { title: '通知中心' },
        },
        {
          path: 'settings',
          name: 'Settings',
          component: () => import('../views/user/settings/SettingsView.vue'),
          meta: { title: '设置' },
        },
        {
          path: 'share',
          name: 'Share',
          component: () => import('../views/user/share/ShareView.vue'),
          meta: { title: '分享' },
        },
      ],
    },
    {
      path: '/500',
      name: '500',
      component: () => import('../views/error/500.vue'),
      meta: { title: '500' },
    },
    {
      path: '/dev/upload-test',
      name: 'UploadTest',
      component: () => import('../views/dev/UploadTest.vue'),
      meta: { title: '上传测试' },
    },
  ],
})

// 路由切换时，若客服当前已打开则关闭并取消导航
router.beforeEach((to, _from, next) => {
  // console.log('Route change detected, checking CS status...', csManager.isOpen)
  // 有 bug ，先注释
  // if (csManager.isOpen) {
  //   csManager.close()
  //   next(false)
  //   return
  // }

  // 未登录时访问受保护页面，跳转到登录页
  const privateRoutes = ['profile', 'orders', 'trade']
  if (privateRoutes.includes(to.name as string) || to.meta.loginRequired) {
    const sessionStore = useSessionStore()
    if (!sessionStore.sessionId) {
      next({ name: 'login' })
      return
    }
  }

  next()
})

// 路由守卫：设置页面标题
router.afterEach((to) => {
  const defaultTitle = import.meta.env.VITE_APP_TITLE || 'KW客户端'
  const pageTitle = to.meta.title as string | undefined

  if (pageTitle) {
    useTitle(`${pageTitle} - ${defaultTitle}`)
  } else {
    useTitle(defaultTitle)
  }
})

export default router
